import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Activity,
  Archive,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FilePlus2,
  HelpCircle,
  Link2Off,
  Mail,
  MailCheck,
  MailWarning,
  Paperclip,
  Phone,
  RefreshCw,
  Send,
  UserCheck,
  XCircle,
} from "lucide-react";
import { fmtDateTime } from "../../lib/format";

const ICONS = {
  "file-plus": FilePlus2,
  send: Send,
  eye: Eye,
  download: Download,
  "help-circle": HelpCircle,
  "check-circle": CheckCircle2,
  "x-circle": XCircle,
  clock: Clock,
  archive: Archive,
  "link-off": Link2Off,
  paperclip: Paperclip,
  phone: Phone,
  refresh: RefreshCw,
  "user-check": UserCheck,
  mail: Mail,
  "mail-check": MailCheck,
  "mail-warning": MailWarning,
  activity: Activity,
};

/**
 * Phase 2B.2 — human-readable Bulgarian activity timeline.
 *
 * Every item is pre-normalized by the backend (`GET /api/quotations/{id}`
 * → `timeline`): title, description, tone, icon, actor display name, test-mode
 * flag and an allowlisted `details` array. No raw metadata is ever rendered and
 * the component never sees tokens, provider ids or recipient addresses.
 */
export default function Timeline({ items }) {
  const { t } = useTranslation();

  if (!items || items.length === 0) {
    return <div className="qw-empty" data-testid="timeline-empty">{t("common.empty")}</div>;
  }

  return (
    <ol className="qw-tl" data-testid="timeline">
      {items.map((it) => {
        const Icon = ICONS[it.icon] || Activity;
        const hasDetails = Array.isArray(it.details) && it.details.length > 0;
        return (
          <li
            key={it.id}
            className={`qw-tl-item tone-${it.tone || "neutral"}`}
            data-testid={`timeline-item-${it.event_type}`}
          >
            <span className="qw-tl-icon" aria-hidden="true">
              <Icon size={15} strokeWidth={2} />
            </span>
            <div className="qw-tl-body">
              <div className="qw-tl-head">
                <span className="qw-tl-title" data-testid={`timeline-title-${it.event_type}`}>
                  {it.title}
                </span>
                {it.test_mode && (
                  <span className="qw-tl-badge test" data-testid={`timeline-test-mode-${it.id}`}>
                    {t("quotations.email_test_mode_badge")}
                  </span>
                )}
              </div>
              {it.description && <div className="qw-tl-desc">{it.description}</div>}
              {it.renewal_link?.quotation_id && (
                <div className="qw-tl-desc">
                  <Link
                    to={`/quotations/${it.renewal_link.quotation_id}`}
                    data-testid={`timeline-renewal-link-${it.id}`}
                    style={{ color: "#0f766e", fontWeight: 600 }}
                  >
                    {it.renewal_link.reference_number || t("quotations.detail")}
                  </Link>
                </div>
              )}
              {it.callback_requested && it.callback_note && (
                <div
                  className="qw-tl-callback"
                  data-testid={`timeline-callback-${it.id}`}
                >
                  <Phone size={13} strokeWidth={2} aria-hidden="true" />
                  <span>{it.callback_note}</span>
                </div>
              )}
              <div className="qw-tl-meta">
                <span data-testid={`timeline-time-${it.id}`}>{fmtDateTime(it.created_at)}</span>
                <span aria-hidden="true">·</span>
                <span data-testid={`timeline-actor-${it.id}`}>{it.actor?.display_name}</span>
              </div>
              {hasDetails && (
                <details className="qw-tl-details" data-testid={`timeline-details-${it.id}`}>
                  <summary>{t("quotations.timeline_details")}</summary>
                  <dl>
                    {it.details.map((d) => (
                      <div key={d.label} className="qw-tl-detail-row">
                        <dt>{d.label}</dt>
                        <dd>{d.value}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
